import React, { Dispatch, FC, SetStateAction, useContext, useEffect, useState } from 'react';
import { AccountContext, AccountContextInterface } from '../../../../contexts';
import { AccountOrganization } from '../../../../models/organization.model';
import { UserSkill } from '../../../../models/skill.model';
import { ButtonLoader } from '../../../../components';

interface FilterModalProps {
  accountId: string;
  applyFilters: () => void;
  filterModalOpen: boolean;
  selectedOrganizations: AccountOrganization[];
  selectedSkills: UserSkill[];
  setSelectedOrganizations: Dispatch<SetStateAction<AccountOrganization[]>>;
  setSelectedSkills: Dispatch<SetStateAction<UserSkill[]>>;
  toggleFilterModal: () => void;
}

const FilterModal: FC<FilterModalProps> = ({
  accountId,
  applyFilters,
  filterModalOpen,
  selectedOrganizations,
  selectedSkills,
  setSelectedOrganizations,
  setSelectedSkills,
  toggleFilterModal,
}) => {
  const { accountService } = useContext<AccountContextInterface>(AccountContext);
  const [organizations, setOrganizations] = useState<AccountOrganization[]>([]);
  const [hasMoreOrganizations, setHasMoreOrganizations] = useState<boolean>(true);
  const [hasMoreSkills, setHasMoreSkills] = useState<boolean>(true);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
  const [loadingSkills, setLoadingSkills] = useState<boolean>(false);
  const [organizationPage, setOrganizationPage] = useState<number>(1);
  const [skillPage, setSkillPage] = useState<number>(1);
  const [skills, setSkills] = useState<UserSkill[]>([]);

  const fetchOrganizations = async (page = 1) => {
    try {
      setLoadingOrganizations(true);
      const { data } = await accountService.getAccountOrganizations(accountId, page, 5);
      if (data) {
        setOrganizations(prev => (page === 1 ? data : [...prev, ...data]));
        setHasMoreOrganizations(data.length === 5);
      }
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const fetchSkills = async (page = 1) => {
    try {
      setLoadingSkills(true);
      const { data } = await accountService.getUserSkills(accountId, page, 5);
      if (data) {
        setSkills(prev => (page === 1 ? data : [...prev, ...data]));
        setHasMoreSkills(data.length === 5);
      }
    } finally {
      setLoadingSkills(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
    fetchSkills();
  }, [accountId]);

  const handleOrgToggle = (org: AccountOrganization) => {
    setSelectedOrganizations(prev => (prev.some(o => o.id === org.id) ? prev.filter(o => o.id !== org.id) : [...prev, org]));
  };

  const handleSkillToggle = (skill: UserSkill) => {
    setSelectedSkills(prev => (prev.some(s => s.id === skill.id) ? prev.filter(s => s.id !== skill.id) : [...prev, skill]));
  };

  const handleLoadMoreOrgs = () => {
    const nextPage = organizationPage + 1;
    setOrganizationPage(nextPage);
    fetchOrganizations(nextPage);
  };

  const handleLoadMoreSkills = () => {
    const nextPage = skillPage + 1;
    setSkillPage(nextPage);
    fetchSkills(nextPage);
  };

  if (!filterModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-10">
      <div className="bg-white max-h-[90vh] max-w-[80vw] overflow-y-auto p-6 rounded-lg w-full md:max-w-[60vw] lg:max-w-[40vw]">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="flex flex-col gap-4">
          <div>
            <label className="font-medium text-sm">Organizations</label>
            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
              {organizations.map(org => (
                <div key={org.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedOrganizations.some(o => o.id === org.id)}
                    onChange={() => handleOrgToggle(org)}
                    className="mr-2"
                  />
                  <span>{org.organization.name}</span>
                </div>
              ))}
              {hasMoreOrganizations && (
                <button onClick={handleLoadMoreOrgs} className="text-blue-500 text-sm mt-1">
                  {loadingOrganizations ? <ButtonLoader /> : 'View More'}
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="font-medium text-sm">Skills</label>
            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
              {skills.map(skill => (
                <div key={skill.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedSkills.some(s => s.id === skill.id)}
                    onChange={() => handleSkillToggle(skill)}
                    className="mr-2"
                  />
                  <span>{skill.skill.name}</span>
                </div>
              ))}
              {hasMoreSkills && (
                <button onClick={handleLoadMoreSkills} className="text-blue-500 text-sm mt-1">
                  {loadingSkills ? <ButtonLoader /> : 'View More'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => {
              setSelectedOrganizations([]);
              setSelectedSkills([]);
            }}
            className="text-red-500 text-sm underline">
            Clear All
          </button>
          <div className="flex gap-2">
            <button onClick={toggleFilterModal} className="text-gray-600 font-medium px-4 py-2 rounded-lg border">
              Close
            </button>
            <button onClick={applyFilters} className="bg-blue-500 text-white font-medium px-4 py-2 rounded-lg">
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
